import {
  addBreadcrumb as sentryAddBreadcrumb,
  captureMessage,
  captureException,
  withScope,
  Scope,
  type SeverityLevel,
} from '@sentry/react';
import { getUser } from '@frontend/auth-helpers';
import { SentryTeamNames } from '@frontend/sentry-teams';
import { getHttpErrorFingerprint, isHttpError, type HttpErrorLike } from './http-error-event';

type WeaveFeatures =
  | 'payments'
  | 'contacts'
  | 'reviews'
  | 'phone'
  | 'messages'
  | 'analytics'
  | 'email'
  | 'lists'
  | 'fax'
  | 'team chat'
  | 'scheduling'
  | 'onboarding'
  | 'auth'
  | 'office-assistant'
  | 'infra'
  | 'user-preferences'
  | 'notifications'
  | 'forms-submissions'
  | 'customer-lifecycle-management'
  | 'ai-receptionist';

type StringWithAutocomplete<T> = T | (string & Record<never, never>);

type SentryTopic = StringWithAutocomplete<WeaveFeatures>;

type LogSeverityLevel = 'log' | 'info';

type LogType =
  /* Describes a generic breadcrumb. This is typically a log message
or user-generated breadcrumb. */
  | 'default'
  /* This is typically a log message. */
  | 'debug'
  /* An error that occurred before the exception. */
  | 'error'
  /* A navigation event can be a URL change in a web application,
or a UI transition in a mobile or desktop application, etc. */
  | 'navigation'
  /* This represents an HTTP request transmitted from your application. */
  | 'http'
  /* Information that helps identify the root cause of the
issue or for whom the error occurred. */
  | 'info'
  /* This represents a query that was made in your application. */
  | 'query'
  /*  Describes a tracing event. */
  | 'transaction'
  /* A user interaction with your app's UI. */
  | 'ui';

type SentryContext = {
  name: string;
  context: Record<string, any>;
};

export interface SentryLog {
  message: string;
  data?: Record<string, unknown>;
  severityLevel?: LogSeverityLevel;
  type?: LogType;
  team: SentryTeamNames;
  category?: SentryTopic | 'log';
}

/**
 * NOTE this doesn't explicity send a message to Sentry, it just adds a breadcrumb, which is like an interesting event that might get sent in an error trace.
 * See https://docs.sentry.io/platforms/javascript/enriching-events/breadcrumbs/
 */
export const addBreadcrumb = ({ message, data, severityLevel = 'info', category = 'log' }: Omit<SentryLog, 'team'>) => {
  sentryAddBreadcrumb({
    category,
    message,
    data,
    level: severityLevel,
  });
};

export const log = ({ message, team, data, severityLevel = 'log', category = 'log' }: SentryLog) => {
  sentryAddBreadcrumb({
    category,
    message,
    data,
    level: severityLevel,
  });
  captureMessage(message, {
    level: severityLevel,
    extra: data,
    tags: {
      team,
    },
  });
};
export interface SentryWarning {
  error: any;
  topic: SentryTopic;
  team: SentryTeamNames;
  addContext?: SentryContext;
  sampleRate?: number; // between 0 and 1. This overrides the global sample rate
  fingerprint?: string[];
}

export const warn = ({ error: warningError, topic, team, addContext, sampleRate, fingerprint }: SentryWarning) => {
  withScope((scope) => {
    setCommonSentryTags(scope, { team, topic, sampleRate });
    scope.setLevel('warning');
    if (!!addContext) scope.setContext(addContext.name, addContext.context);
    if (fingerprint) {
      scope.setFingerprint(fingerprint);
    } else if (isHttpError(warningError)) {
      scope.setFingerprint(getHttpErrorFingerprint(warningError));
    }

    if (warningError instanceof Error) {
      captureException(warningError);
      return;
    }

    captureMessage(String(warningError));
  });
};

type ErrorSeverityLevel = 'error' | 'fatal';

export interface SentryError {
  error: any;
  topic: SentryTopic;
  team: SentryTeamNames;
  severityLevel?: Extract<SeverityLevel, ErrorSeverityLevel>;
  addContext?: SentryContext;
  sampleRate?: number; // between 0 and 1. This overrides the global sample rate
  /**
   * Overrides Sentry's default (stack-trace based) grouping.
   * Useful when several distinct failure modes are reported from the same call site and would
   * otherwise collapse into a single umbrella issue.
   */
  fingerprint?: string[];
}

export const error = ({
  error: capturedError,
  team,
  topic,
  severityLevel = 'error',
  addContext,
  sampleRate,
  fingerprint,
}: SentryError) => {
  withScope((scope) => {
    setCommonSentryTags(scope, { team, topic, sampleRate });
    scope.setLevel(severityLevel);
    if (!!addContext) scope.setContext(addContext.name, addContext.context);
    if (fingerprint) {
      scope.setFingerprint(fingerprint);
    } else if (isHttpError(capturedError)) {
      scope.setFingerprint(getHttpErrorFingerprint(capturedError));
    }
    captureException(capturedError);
  });
};

export interface SentryHttpError {
  error: HttpErrorLike;
  topic: SentryTopic;
  team: SentryTeamNames;
  addContext?: SentryContext;
  sampleRate?: number;
  severityLevel?: Extract<SeverityLevel, 'warning' | 'error' | 'fatal'>;
  fingerprint?: string[];
}

export const httpError = ({
  error: capturedError,
  team,
  topic,
  addContext,
  sampleRate,
  severityLevel = 'error',
  fingerprint,
}: SentryHttpError) => {
  withScope((scope) => {
    setCommonSentryTags(scope, { team, topic, sampleRate });
    scope.setTag('error.kind', 'http');
    scope.setLevel(severityLevel);
    if (!!addContext) scope.setContext(addContext.name, addContext.context);
    scope.setFingerprint(fingerprint || getHttpErrorFingerprint(capturedError));
    captureException(capturedError);
  });
};

function setCommonSentryTags(scope: Scope, tags: { topic: string; team: string; sampleRate?: number }) {
  const userId = getUser()?.userID;
  if (userId) {
    scope.setUser({ id: userId });
  } else {
    scope.setUser(null); // clear the user context if there is no user, to avoid incorrectly associating errors with a previous user
  }
  scope.setTag('topic', tags.topic);
  scope.setTag('team', tags.team);
  if (tags.sampleRate !== undefined) {
    scope.setContext('meta', { sampleRate: tags.sampleRate });
  }
}
